/**
 * Central de Notificações em Tempo Real da UCC.
 *
 * Exibe alertas visuais para a staff e organizadores:
 *  - Inscrições marcadas como pagas (Pix a conferir)
 *  - Novas inscrições de equipes
 *  - Tickets de suporte
 *  - Confirmações de vagas
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';

export interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string | null;
  read: boolean;
  createdAt: number;
}

interface NotificationConfig {
  icon: (className?: string) => ReactNode;
  tone: 'gold' | 'win' | 'loss' | 'warn';
  badge: string;
}

const TYPE_CONFIG: Record<string, NotificationConfig> = {
  payment_pending: {
    icon: (cls = 'size-4 text-gold-ink') => (
      <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    tone: 'gold',
    badge: 'Pix a Conferir',
  },
  registration_new: {
    icon: (cls = 'size-4 text-amber-400') => (
      <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <line x1="19" x2="19" y1="8" y2="14" />
        <line x1="22" x2="16" y1="11" y2="11" />
      </svg>
    ),
    tone: 'warn',
    badge: 'Nova Inscrição',
  },
  registration_confirmed: {
    icon: (cls = 'size-4 text-emerald-400') => (
      <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
        <path d="M4 22h16" />
        <path d="M10 14.66V17c0 .55-.45 1-1 1H7v2h10v-2h-2c-.55 0-1-.45-1-1v-2.34c0-.98.54-1.89 1.4-2.36l1.6-.88c1.86-1.02 3-2.98 3-5.1V4H4v4.32c0 2.12 1.14 4.08 3 5.1l1.6.88c.86.47 1.4 1.38 1.4 2.36z" />
      </svg>
    ),
    tone: 'win',
    badge: 'Vaga Confirmada',
  },
  ticket_new: {
    icon: (cls = 'size-4 text-amber-400') => (
      <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" x2="12" y1="8" y2="12" />
        <line x1="12" x2="12.01" y1="16" y2="16" />
      </svg>
    ),
    tone: 'warn',
    badge: 'Suporte',
  },
  general: {
    icon: (cls = 'size-4 text-gold-ink') => (
      <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
    tone: 'gold',
    badge: 'Aviso',
  },
};

function formatRelativeTime(epochSeconds: number): string {
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - epochSeconds);
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export function NotificationCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Polling a cada 15 segundos se o usuário estiver logado
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<{ notifications: NotificationItem[]; unreadCount: number }>('/notifications'),
    enabled: Boolean(user),
    refetchInterval: 15_000,
  });

  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  const markAllMutation = useMutation({
    mutationFn: () => api('/notifications/read-all', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markSingleMutation = useMutation({
    mutationFn: (id: string) => api(`/notifications/${id}/read`, { method: 'PATCH' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Fechar ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  if (!user) return null;

  function handleItemClick(notif: NotificationItem) {
    if (!notif.read) {
      markSingleMutation.mutate(notif.id);
    }
    setIsOpen(false);
    if (notif.link) {
      navigate(notif.link);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Botão de Sino de Notificações */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Abrir central de notificações"
        className={cn(
          'relative flex size-10 items-center justify-center rounded-lg border text-muted transition-all',
          isOpen
            ? 'border-gold bg-gold/10 text-gold-ink'
            : 'border-line hover:border-gold/50 hover:text-paper hover:bg-surface-2',
        )}
      >
        <svg className="size-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {unreadCount > 0 && (
          <span className="absolute -right-1.5 -top-1.5 flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-gold px-1 text-[0.6875rem] font-black text-black shadow-md animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Popover */}
      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-[min(24rem,calc(100vw-2rem))] rounded-xl border border-gold/30 bg-surface p-0 shadow-[var(--shadow-lift)]">
          {/* Cabeçalho */}
          <header className="flex items-center justify-between border-b border-line px-4 py-3">
            <div className="flex items-center gap-2">
              <h3 className="ucc-display text-sm font-semibold text-paper">Notificações</h3>
              {unreadCount > 0 && (
                <span className="rounded-full bg-gold/20 px-2 py-0.5 text-[0.6875rem] font-bold text-gold-ink">
                  {unreadCount} nova{unreadCount > 1 ? 's' : ''}
                </span>
              )}
            </div>

            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllMutation.mutate()}
                disabled={markAllMutation.isPending}
                className="text-[0.6875rem] font-medium text-gold-ink hover:underline hover:text-gold-ink-strong transition-colors"
              >
                Marcar todas como lidas
              </button>
            )}
          </header>

          {/* Lista de Notificações */}
          <div className="max-h-[22rem] divide-y divide-line/40 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <span className="mx-auto mb-2 flex size-10 items-center justify-center rounded-full bg-surface-2 text-faint">
                  <svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
                  </svg>
                </span>
                <p className="text-xs text-muted">Nenhuma notificação por enquanto.</p>
              </div>
            ) : (
              notifications.map((item) => {
                const config = (TYPE_CONFIG[item.type] ?? TYPE_CONFIG['general'])!;

                return (
                  <div
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 p-3.5 transition-colors hover:bg-surface-2/80',
                      !item.read ? 'bg-gold/5' : 'opacity-80',
                    )}
                  >
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-2">
                      {config.icon()}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={cn(
                            'text-[0.625rem] font-bold uppercase tracking-wider',
                            config.tone === 'gold' && 'text-gold-ink',
                            config.tone === 'win' && 'text-emerald-400',
                            config.tone === 'warn' && 'text-amber-400',
                            config.tone === 'loss' && 'text-rose-400',
                          )}
                        >
                          {config.badge}
                        </span>
                        <span className="text-[0.625rem] text-faint">
                          {formatRelativeTime(item.createdAt)}
                        </span>
                      </div>

                      <p className="mt-0.5 text-xs font-medium text-paper leading-snug">
                        {item.title}
                      </p>
                      <p className="mt-0.5 text-[0.6875rem] text-muted line-clamp-2 leading-relaxed">
                        {item.message}
                      </p>
                    </div>

                    {!item.read && (
                      <span className="mt-2 size-1.5 shrink-0 rounded-full bg-gold shadow-[0_0_6px_var(--color-gold)]" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
